/**
 * 建房表单模型。纯函数 + 纯数据，没有 React，可以直接单测。
 *
 * 校验是服务端 `sanitizeRoomConfig` 的**前置镜像**：服务端会把越界值**静默钳回去**
 * （红线 #38：配置由服务端决定），所以本地不拦的话，用户填 100 起始筹码、
 * 点完建房进去发现变成了 200，还以为是 bug。这里提前把同样的边界讲清楚。
 *
 * 与本地传手机的 `localSetup.ts` 刻意分开：两者的默认值、边界、字段都不一样
 * （联机没有「每个座位的昵称」，人数是 maxSeats 而不是实到人数），
 * 合并只会得到一个到处是 if (online) 的表单模型。
 */
import { ROOM_DEFAULTS, SEAT_LIMITS, type Chips, type DeckVariant, type RoomConfig } from '@poker/shared'

export interface RoomBlindPreset {
  readonly label: string
  readonly smallBlind: Chips
  readonly bigBlind: Chips
}

/** 任务书指定的三档。5/10 不给：起手就想打小的人直接用自定义。 */
export const ROOM_BLIND_PRESETS: readonly RoomBlindPreset[] = [
  { label: '10 / 20', smallBlind: 10, bigBlind: 20 },
  { label: '25 / 50', smallBlind: 25, bigBlind: 50 },
  { label: '50 / 100', smallBlind: 50, bigBlind: 100 },
]

export const ROOM_STACK_PRESETS: readonly Chips[] = [1000, 2000, 5000, 10_000]

/** 玩法：建房时定，进房后不能改（服务端 CONFIG_MUTABILITY 的 createOnly） */
export const DECK_VARIANT_CHOICES: readonly { readonly value: DeckVariant; readonly label: string }[] = [
  { value: 'standard', label: '标准' },
  { value: 'shortDeck', label: '短牌' },
]

/** 2~8：`SEAT_LIMITS` 就是服务端钳位的那两个数，不另写常量。 */
export const SEAT_COUNT_CHOICES: readonly number[] = Array.from(
  { length: SEAT_LIMITS.MAX - SEAT_LIMITS.MIN + 1 },
  (_, i) => SEAT_LIMITS.MIN + i,
)

/** 服务端 `sanitizeRoomConfig` 的硬下限：startingStack ≥ bigBlind × 10。 */
export const SERVER_MIN_STACK_IN_BB = 10
/** 建议下限：20 个大盲以下第一手就有人被盲注推成全下，牌局展不开。 */
export const COMFORTABLE_STACK_IN_BB = 20
export const MAX_STACK = 10_000_000
export const MAX_SMALL_BLIND = 100_000
export const MAX_BIG_BLIND = 200_000
/** RoomConfig.name 服务端 slice(0, 32) */
export const ROOM_NAME_MAX = 32

export interface CreateRoomForm {
  /** 桌子有几个座位（= RoomConfig.maxSeats），不是当前实到人数 */
  readonly maxSeats: number
  readonly smallBlind: Chips
  readonly bigBlind: Chips
  readonly startingStack: Chips
  readonly deckVariant: DeckVariant
  /** 盲注/筹码是否走自定义输入（影响分段控件的高亮，不影响提交的值） */
  readonly customBlinds: boolean
  readonly customStack: boolean
}

export const DEFAULT_CREATE_FORM: CreateRoomForm = {
  maxSeats: ROOM_DEFAULTS.maxSeats, // 6
  smallBlind: ROOM_DEFAULTS.smallBlind, // 10
  bigBlind: ROOM_DEFAULTS.bigBlind, // 20
  startingStack: ROOM_DEFAULTS.startingStack, // 2000
  deckVariant: ROOM_DEFAULTS.deckVariant, // standard
  customBlinds: false,
  customStack: false,
}

export function setMaxSeats(f: CreateRoomForm, n: number): CreateRoomForm {
  const clamped = Math.min(SEAT_LIMITS.MAX, Math.max(SEAT_LIMITS.MIN, Math.trunc(n)))
  return { ...f, maxSeats: clamped }
}

export function setBlindPreset(f: CreateRoomForm, p: RoomBlindPreset): CreateRoomForm {
  return { ...f, smallBlind: p.smallBlind, bigBlind: p.bigBlind, customBlinds: false }
}

/** 自定义盲注：不在这里钳位，让校验去报错——边打字边被改数字是最烦人的输入体验。 */
export function setCustomBlinds(f: CreateRoomForm, smallBlind: Chips, bigBlind: Chips): CreateRoomForm {
  return { ...f, smallBlind, bigBlind, customBlinds: true }
}

export function setCreateStack(f: CreateRoomForm, n: Chips, custom = false): CreateRoomForm {
  return { ...f, startingStack: n, customStack: custom }
}

export function setDeckVariant(f: CreateRoomForm, deckVariant: DeckVariant): CreateRoomForm {
  return { ...f, deckVariant: deckVariant === 'shortDeck' ? 'shortDeck' : 'standard' }
}

export function matchesBlindPreset(f: CreateRoomForm, p: RoomBlindPreset): boolean {
  return !f.customBlinds && f.smallBlind === p.smallBlind && f.bigBlind === p.bigBlind
}

/** 起始筹码折合多少个大盲；大盲非法时返回 0（调用方只拿它做展示）。 */
export function stackInBigBlinds(f: CreateRoomForm): number {
  if (!Number.isInteger(f.bigBlind) || f.bigBlind <= 0) return 0
  return Math.floor(f.startingStack / f.bigBlind)
}

/** 返回错误文案数组；空数组 = 可以建房。文案要具体到「该改哪一项、改成多少」。 */
export function validateCreateForm(f: CreateRoomForm): string[] {
  const errors: string[] = []

  if (!Number.isInteger(f.maxSeats) || f.maxSeats < SEAT_LIMITS.MIN || f.maxSeats > SEAT_LIMITS.MAX) {
    errors.push(`人数必须在 ${SEAT_LIMITS.MIN}~${SEAT_LIMITS.MAX} 之间`)
  }
  if (!Number.isInteger(f.smallBlind) || f.smallBlind < 1) {
    errors.push('小盲必须是不小于 1 的整数')
  } else if (f.smallBlind > MAX_SMALL_BLIND) {
    errors.push(`小盲最大 ${MAX_SMALL_BLIND}`)
  }
  if (!Number.isInteger(f.bigBlind) || f.bigBlind < 2) {
    errors.push('大盲必须是不小于 2 的整数')
  } else if (f.bigBlind > MAX_BIG_BLIND) {
    errors.push(`大盲最大 ${MAX_BIG_BLIND}`)
  }
  // ★ 服务端会把 bigBlind 直接抬到 max(smallBlind, bigBlind)，本地必须先讲清楚
  if (Number.isInteger(f.smallBlind) && Number.isInteger(f.bigBlind) && f.smallBlind > f.bigBlind) {
    errors.push('小盲不能大于大盲')
  }
  if (!Number.isInteger(f.startingStack) || f.startingStack < 1) {
    errors.push('起始筹码必须是正整数')
  } else if (f.startingStack > MAX_STACK) {
    errors.push(`起始筹码最大 ${MAX_STACK}`)
  } else if (Number.isInteger(f.bigBlind) && f.bigBlind >= 2 && f.startingStack < f.bigBlind * SERVER_MIN_STACK_IN_BB) {
    // 这条是服务端的硬钳位，不拦就会「填了 100、进去变成 200」
    errors.push(`起始筹码至少要有 ${SERVER_MIN_STACK_IN_BB} 个大盲（${f.bigBlind * SERVER_MIN_STACK_IN_BB}）`)
  }
  return errors
}

export function canCreate(f: CreateRoomForm): boolean {
  return validateCreateForm(f).length === 0
}

/** 非阻塞的建议（不拦提交，只提醒）。 */
export function createFormWarnings(f: CreateRoomForm): string[] {
  const out: string[] = []
  const bb = f.bigBlind
  if (
    Number.isInteger(bb) &&
    bb >= 2 &&
    f.startingStack >= bb * SERVER_MIN_STACK_IN_BB &&
    f.startingStack < bb * COMFORTABLE_STACK_IN_BB
  ) {
    out.push(`只有 ${stackInBigBlinds(f)} 个大盲，第一手就可能有人被盲注顶成全下`)
  }
  return out
}

/** 房间名：顶栏和房间列表都用它。空昵称时退回 ROOM_DEFAULTS.name（服务端会当成无名房）。 */
export function roomNameFor(nickname: string): string {
  const trimmed = nickname.trim()
  if (trimmed === '') return ROOM_DEFAULTS.name
  return Array.from(`${trimmed}的房间`).slice(0, ROOM_NAME_MAX).join('')
}

/**
 * 表单 → `createRoom.config`。
 *
 * 只提交用户真正在这个页面上决定过的字段，其余一律不带——服务端有完整默认值，
 * 客户端把 ROOM_DEFAULTS 整份抄过去只会在将来默认值变更时把旧值钉死。
 *
 * `rebuyAmount` 跟着 `startingStack` 走：补码补成和开局一样多是唯一不需要解释的规则
 * （服务端对它的钳位同样是 ≥ bigBlind × 10）。
 *
 * 玩法只在选了短牌时才带：标准局不带这个键，和加这个字段之前发出去的消息逐字节一样。
 */
export function toCreateRoomConfig(f: CreateRoomForm, nickname = ''): Partial<RoomConfig> {
  return {
    name: roomNameFor(nickname),
    maxSeats: f.maxSeats,
    smallBlind: f.smallBlind,
    bigBlind: f.bigBlind,
    startingStack: f.startingStack,
    rebuyAmount: f.startingStack,
    ...(f.deckVariant === 'shortDeck' ? { deckVariant: 'shortDeck' as const } : {}),
  }
}
